<template>
  <el-dialog
    v-model="visible"
    title="设备转移"
    width="500px"
    :close-on-click-modal="false"
    destroy-on-close
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
    >
      <!-- 当前设备信息 -->
      <el-alert
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 20px"
      >
        <template #title>
          <span>当前设备：{{ device?.deviceName }} ({{ device?.deviceId }})</span>
        </template>
      </el-alert>

      <!-- 原检测员信息 -->
      <el-form-item label="原检测员">
        <el-input
          v-if="device?.currentInspector"
          :value="`${device.currentInspector.nickName} (${device.currentInspector.phone})`"
          disabled
        />
        <el-input
          v-else
          value="未绑定"
          disabled
        />
      </el-form-item>

      <!-- 选择新检测员 -->
      <el-form-item label="转移至" prop="toInspectorId">
        <el-input
          v-model.number="formData.toInspectorId"
          placeholder="请输入新检测员ID"
          clearable
          style="width: 100%"
        />
      </el-form-item>

      <!-- 数据继承选项 -->
      <el-form-item label="数据继承">
        <el-checkbox v-model="formData.inheritData">
          继承历史数据
          <el-tooltip content="勾选后，新检测员可以查看该设备的完整历史数据" placement="top">
            <el-icon><QuestionFilled /></el-icon>
          </el-tooltip>
        </el-checkbox>
        <div class="form-tip">
          勾选后，新检测员可以查看该设备的所有历史数据（包括原检测员的数据）
        </div>
      </el-form-item>

      <!-- 备注 -->
      <el-form-item label="备注">
        <el-input
          v-model="formData.remark"
          type="textarea"
          :rows="3"
          placeholder="请输入转移原因（选填）"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>

      <!-- 警告信息 -->
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        style="margin-top: 10px"
      >
        <template #title>
          <span>设备转移后，原检测员将失去该设备的访问权限</span>
        </template>
      </el-alert>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="submitLoading"
          @click="handleSubmit"
        >
          确认转移
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { QuestionFilled } from '@element-plus/icons-vue'
import { transferDevice } from '@/api/device'

export default {
  name: 'DeviceTransferDialog',
  components: {
    QuestionFilled
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    device: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const formRef = ref(null)
    const submitLoading = ref(false)

    const formData = reactive({
      deviceId: '',
      fromInspectorId: null,
      toInspectorId: null,
      inheritData: true,
      remark: ''
    })

    const rules = {
      toInspectorId: [
        { required: true, message: '请输入新检测员ID', trigger: 'blur' }
      ]
    }

    // 同步 visible
    const visible = computed({
      get: () => props.visible,
      set: (val) => emit('update:visible', val)
    })

    // 监听 visible 变化
    watch(() => props.visible, (val) => {
      if (val) {
        // 重置表单
        formData.deviceId = props.device?.deviceId || ''
        formData.fromInspectorId = props.device?.currentInspector?.id || null
        formData.toInspectorId = null
        formData.inheritData = true
        formData.remark = ''
      }
    }, { immediate: true })

    // 提交表单
    const handleSubmit = async () => {
      try {
        await formRef.value.validate()

        // 验证原检测员
        if (!formData.fromInspectorId) {
          ElMessage.warning('该设备尚未绑定，无法进行转移操作')
          return
        }

        // 验证不能转移给自己
        if (formData.toInspectorId === formData.fromInspectorId) {
          ElMessage.warning('不能将设备转移给原检测员')
          return
        }

        submitLoading.value = true

        const response = await transferDevice({
          deviceId: formData.deviceId,
          fromInspectorId: formData.fromInspectorId,
          toInspectorId: formData.toInspectorId,
          inheritData: formData.inheritData,
          remark: formData.remark
        })

        if (response.code === 200) {
          ElMessage.success('设备转移成功')
          emit('success')
          visible.value = false
        } else {
          ElMessage.error(response.message || '设备转移失败')
        }
      } catch (error) {
        console.error('设备转移失败：', error)
        ElMessage.error('设备转移失败')
      } finally {
        submitLoading.value = false
      }
    }

    return {
      formRef,
      visible,
      submitLoading,
      formData,
      rules,
      handleSubmit
    }
  }
}
</script>

<style lang="scss" scoped>
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
